import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Tabs,
  Tab,
  Card,
  CardContent,
  Grid,
  Alert,
  CircularProgress,
  Button,
  Chip,
  Paper,
  useTheme,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Dashboard,
  People,
  Email,
  EmojiEvents,
  Settings,
  Analytics,
  Security,
  ExitToApp,
  Refresh,
  Assessment,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import UsersManagement from '../components/admin/UsersManagement';
import ContactManagement from '../components/admin/ContactManagement';
import LeaderboardManagement from '../components/admin/LeaderboardManagement';
import AnalyticsDashboard from '../components/admin/AnalyticsDashboard';
import CompetitionSettings from '../components/admin/CompetitionSettings';
import TraderStatisticsReports from '../components/admin/TraderStatisticsReports';

// Admin email list - you can modify this
const ADMIN_EMAILS = [
  'admin@t1fxteam.co.za',
  'support@t1fxteam.co.za',
  'stephan@t1fxteam.co.za',
  'monique@t1fxteam.co.za',
  'walter@t1fxteam.co.za',
  'dewald@t1fxteam.co.za',
  // Add more admin emails here
];

// Special access for trader statistics reports
const TRADER_STATS_ACCESS = 'stephan@t1fxteam.co.za';

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`admin-tabpanel-${index}`}
      aria-labelledby={`admin-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const AdminPortalPage = () => {
  const [currentTab, setCurrentTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [adminVerified, setAdminVerified] = useState(false);
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();

  useEffect(() => {
    // Check if user is authenticated and is an admin
    if (!currentUser) {
      navigate('/login');
      return;
    }

    // Check if user email is in admin list
    const isAdmin = ADMIN_EMAILS.includes(currentUser.email);
    setAdminVerified(isAdmin);
    setLoading(false);

    if (!isAdmin) {
      // Redirect non-admin users
      setTimeout(() => {
        navigate('/');
      }, 3000);
    }
  }, [currentUser, navigate]);

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const adminTabs = [
    { label: 'Dashboard', icon: <Dashboard />, component: AnalyticsDashboard },
    { label: 'Users', icon: <People />, component: UsersManagement },
    { label: 'Contact Messages', icon: <Email />, component: ContactManagement },
    { label: 'Leaderboard', icon: <EmojiEvents />, component: LeaderboardManagement },
    { label: 'Competition Settings', icon: <Settings />, component: CompetitionSettings },
  ];

  // Add Trader Statistics Reports tab only for authorized user
  if (currentUser?.email === TRADER_STATS_ACCESS) {
    adminTabs.push({
      label: 'Trader Statistics Reports',
      icon: <Assessment />,
      component: TraderStatisticsReports,
    });
  }

  if (loading) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
        }}
      >
        <CircularProgress size={60} sx={{ color: 'white' }} />
      </Box>
    );
  }

  if (!adminVerified) {
    return (
      <Box sx={{ pt: 12, pb: 8, minHeight: '100vh', backgroundColor: '#f5f7fa' }}>
        <Container maxWidth="md">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Alert severity="error" sx={{ mb: 4 }}>
              <Typography variant="h6" gutterBottom>
                Access Denied
              </Typography>
              <Typography>
                You don't have administrator privileges to access this page. 
                You will be redirected to the home page in a few seconds.
              </Typography>
            </Alert>
            <Box textAlign="center">
              <Button variant="contained" onClick={() => navigate('/')}>
                Go to Home Page
              </Button>
            </Box>
          </motion.div>
        </Container>
      </Box>
    );
  }

  const CurrentTabComponent = adminTabs[currentTab].component;

  return (
    <Box sx={{ pt: 8, minHeight: '100vh', backgroundColor: '#f8fafc' }}>
      {/* Admin Header */}
      <Box sx={{ 
        backgroundColor: 'white', 
        borderBottom: 1, 
        borderColor: 'divider',
        position: 'sticky',
        top: 64, // Account for navbar height
        zIndex: 100,
        boxShadow: 1
      }}>
        <Container maxWidth="xl">
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 2 }}>
            <Box>
              <Typography variant="h4" fontWeight="bold" color="primary">
                Admin Portal
              </Typography>
              <Typography variant="body2" color="text.secondary">
                FX Wars Competition Management System
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Chip 
                icon={<Security />} 
                label={`Admin: ${currentUser?.email}`} 
                color="primary" 
                variant="outlined" 
              />
              <Tooltip title="Refresh Data">
                <IconButton onClick={() => window.location.reload()}>
                  <Refresh />
                </IconButton>
              </Tooltip>
              <Tooltip title="Logout">
                <IconButton onClick={handleLogout} color="error">
                  <ExitToApp />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          
          {/* Admin Navigation Tabs */}
          <Tabs 
            value={currentTab} 
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ 
              borderBottom: 1, 
              borderColor: 'divider',
              '& .MuiTab-root': {
                minHeight: 60,
                textTransform: 'none',
                fontSize: '0.95rem',
                fontWeight: 500,
              }
            }}
          >
            {adminTabs.map((tab, index) => (
              <Tab 
                key={index}
                icon={tab.icon} 
                label={tab.label} 
                iconPosition="start"
                sx={{ 
                  '& .MuiSvgIcon-root': { 
                    mr: 1,
                    fontSize: '1.2rem'
                  }
                }}
              />
            ))}
          </Tabs>
        </Container>
      </Box>

      {/* Tab Content */}
      <Container maxWidth="xl" sx={{ mt: 0 }}>
        {adminTabs.map((tab, index) => (
          <TabPanel key={index} value={currentTab} index={index}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
            >
              <CurrentTabComponent />
            </motion.div>
          </TabPanel>
        ))}
      </Container>
    </Box>
  );
};

export default AdminPortalPage; 